<template>
	<div class="phone-input" :class="{ 'phone-input--error': !!errorMessage }">
		<!--
			One input, two modes. intl-tel-input is attached to it normally; for a country
			it has no dial code for the instance is destroyed and the same element carries
			on as a plain box, because a flag control that keeps the previous country does
			not just look wrong - it builds the number from it. See setCountry.
		-->
		<input
			ref="input"
			type="tel"
			:name="name"
			:required="required"
			:disabled="disabled"
			autocomplete="tel"
			class="input"
			@blur="onBlur" />
		<p v-if="plainMode && !errorMessage" class="help">
{{ config.plainHint }}
</p>
		<p v-if="errorMessage" class="help is-danger">
{{ errorMessage }}
</p>
	</div>
</template>

<script>
	// intl-tel-input v23 — EAE'nin iletisim formundaki bileşenin karşılığı.
	// WithUtils paketi kullanıldığı için CDN'den ayrıca utils.js çekilmiyor.
	import intlTelInput from 'intl-tel-input/build/js/intlTelInputWithUtils.js';
	import 'intl-tel-input/build/css/intlTelInput.css';
	import trTranslations from 'intl-tel-input/build/js/i18n/tr/index.js';
	import enTranslations from 'intl-tel-input/build/js/i18n/en/index.js';

	// The CRM identifies a country by a SAP code, which is its ISO 3166-1 alpha-2 code for
	// 232 of the 253 countries it returns. These four are real countries where it is not.
	// The rest of the mismatches have no dial code at all - Antarctica, NATO, the European
	// Union - so there is nothing to map them to.
	const SAP_TO_ISO2 = {
		tp: 'tl', // Doğu Timor
		ko: 'xk', // Kosova
		mne: 'me', // Montenegro
		srb: 'rs' // Sırbistan
	};

	/**
	 * What a plain-mode number has to look like: a +, a country code and a number, and no
	 * more than 30 characters in total. That is EAE's own rule for the field, and it is
	 * deliberately no stricter - there is no dial-code data for these countries, so there
	 * is nothing to check the number against. The CRM has the last word, and since the
	 * push became synchronous its refusal comes back under this same field.
	 */
	const PLAIN_NUMBER = /^\+\d{7,20}$/;
	const PLAIN_MAX_LENGTH = 30;

	// TR ve EN sayfaların birbirinden ayrıldığı tek yer burası.
	const LOCALE_CONFIG = {
		tr: {
			initialCountry: 'tr',
			maxlength: null,
			i18n: trTranslations,
			invalidMessage: 'Geçerli bir telefon numarası giriniz.',
			requiredMessage: 'Bu alan zorunludur.',
			plainHint: 'Numarayı ülke koduyla birlikte yazınız, ör. +90 532 123 45 67',
			plainInvalidMessage: 'Numarayı + ve ülke koduyla, en fazla 30 karakter olarak yazınız.'
		},
		en: {
			// No country until the form has one. The field used to open on the United
			// States, flag, +1 and a 201-555 placeholder, which reads as an answer the
			// visitor never gave - and on a form whose first question is the country.
			initialCountry: '',
			// Türkiye used to be excluded here to mirror EAE's own English contact form. Our
			// form is not the same shape: its country dropdown lists every country in both
			// languages, Türkiye included, and the dial code is expected to follow that
			// choice. Excluding it made the one country a visitor could pick but not dial.
			maxlength: 15,
			i18n: enTranslations,
			invalidMessage: 'Please enter a valid phone number.',
			requiredMessage: 'This field is required.',
			plainHint: 'Enter the number with its country code, e.g. +90 532 123 45 67',
			plainInvalidMessage: 'Enter the number with a + and its country code, at most 30 characters.'
		}
	};

	export default {
		name: 'PhoneInput',
		props: {
			// E.164 formatında numara, ör. +905012345678
			value: {
				type: String,
				default: ''
			},
			// 'tr' | 'en'. Verilmezse localStorage'daki lang'den türetilir.
			locale: {
				type: String,
				default: null
			},
			name: {
				type: String,
				default: 'phone_number'
			},
			required: {
				type: Boolean,
				default: false
			},
			disabled: {
				type: Boolean,
				default: false
			},
			// EAE'deki davranış: numara geçerli hale gelince fazladan rakam girilemiyor.
			stopAtValid: {
				type: Boolean,
				default: true
			},
			/**
			 * Offer Türkiye and nothing else. This used to live in the Turkish locale, which
			 * was wrong: registration fixes the country to Türkiye and renders it disabled,
			 * but the profile screen lets a Turkish member pick any country - and there the
			 * restriction meant setCountry silently failed for every one of them. It belongs
			 * to the screen, not the language.
			 */
			lockToTurkiye: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				iti: null,
				errorMessage: '',
				lastEmitted: '',
				// True once setCountry has been handed a country intl-tel-input has no dial
				// code for. The instance is destroyed and the same input carries on plain.
				plainMode: false
			};
		},
		computed: {
			resolvedLocale() {
				if (this.locale) {
					return LOCALE_CONFIG[this.locale] ? this.locale : 'en';
				}
				const lang = window.localStorage.getItem('lang') || 'tr-TR';
				return lang.toLowerCase().startsWith('tr') ? 'tr' : 'en';
			},
			config() {
				return LOCALE_CONFIG[this.resolvedLocale];
			}
		},
		watch: {
			resolvedLocale() {
				this.destroyIti();
				this.plainMode = false;
				this.$nextTick(this.initIti);
			},
			value(next) {
				if (next === this.lastEmitted) {
					return;
				}
				if (this.plainMode) {
					this.writePlain(next || '');
				} else if (this.iti) {
					this.iti.setNumber(next || '');
				}
			}
		},
		mounted() {
			this.initIti();
		},
		beforeDestroy() {
			this.destroyIti();
		},
		methods: {
			initIti() {
				const input = this.$refs.input;
				const config = this.config;

				if (config.maxlength) {
					input.setAttribute('maxlength', String(config.maxlength));
				} else {
					input.removeAttribute('maxlength');
				}

				const options = {
					separateDialCode: true,
					autoPlaceholder: 'aggressive',
					initialCountry: config.initialCountry,
					i18n: config.i18n
				};
				if (this.lockToTurkiye) options.onlyCountries = ['tr'];

				this.iti = intlTelInput(input, options);

				// Bayrak + kod kutusu ile input'un tek satırda ve tam genişlikte durması için.
				if (input.parentNode.classList.contains('iti')) {
					input.parentNode.style.width = '100%';
					input.parentNode.style.display = 'flex';
				}

				if (this.value) {
					this.iti.setNumber(this.value);
				}

				input.addEventListener('keydown', this.onKeydown);
				input.addEventListener('input', this.onInput);
				input.addEventListener('countrychange', this.onCountryChange);
			},
			destroyIti() {
				const input = this.$refs.input;
				if (input) {
					input.removeEventListener('keydown', this.onKeydown);
					input.removeEventListener('input', this.onInput);
					input.removeEventListener('countrychange', this.onCountryChange);
				}
				if (this.iti) {
					this.iti.destroy();
					this.iti = null;
				}
			},
			onKeydown(e) {
				if (!this.stopAtValid || !this.iti) return;
				if (!/^\d$/.test(e.key)) return;
				// Seçili aralığın üzerine yazılıyorsa engelleme.
				const el = e.target;
				if (el.selectionStart !== el.selectionEnd) return;
				try {
					if (this.iti.isValidNumber()) e.preventDefault();
				} catch (err) { /* utils henüz hazır değilse yoksay */ }
			},
			onInput() {
				const el = this.$refs.input;
				el.value = this.applyPlaceholderMask(el.value, el.getAttribute('placeholder') || '');

				const hasDigits = el.value.replace(/\D/g, '').length > 0;
				if (hasDigits && !this.isValid()) {
					this.errorMessage = this.config.invalidMessage;
				} else {
					this.errorMessage = '';
				}

				this.emitValue();
			},
			onCountryChange() {
				this.onInput();
			},

			/* ---------- plain mode ---------- */

			/**
			 * Hands the input over to the visitor. intl-tel-input is destroyed, which puts
			 * the element back where it was and takes the flag box with it, and a plain
			 * listener replaces the masking one. Whatever was typed is carried across as
			 * digits so a half-entered number is not thrown away.
			 */
			enterPlainMode() {
				if (this.plainMode) return;

				const el = this.$refs.input;
				// getNumber(), not el.value: with separateDialCode the input holds only the
				// national part and the +995 lives in the flag box, which is about to be
				// destroyed. Taking the input alone truncated a member's stored number to
				// its last nine digits. Falls back to the input for a half-typed number
				// that does not parse yet.
				const full = this.iti ? (this.iti.getNumber() || (el ? el.value : '')) : (el ? el.value : '');
				const carried = this.normalisePlain(full);

				this.destroyIti();
				this.plainMode = true;
				this.errorMessage = '';

				if (el) {
					el.setAttribute('maxlength', String(PLAIN_MAX_LENGTH));
					el.removeAttribute('placeholder');
					el.value = carried;
					el.addEventListener('input', this.onPlainInput);
				}

				this.emitValue();
			},

			/**
			 * Gives the flag control the input back. What was typed survives the round trip
			 * through `value`: initIti feeds the prop back in with setNumber, and the prop
			 * is what plain mode has been emitting all along.
			 */
			leavePlainMode() {
				if (!this.plainMode) return;

				const el = this.$refs.input;
				if (el) {
					el.removeEventListener('input', this.onPlainInput);
					el.value = '';
				}

				this.plainMode = false;
				this.errorMessage = '';
				this.initIti();
			},

			onPlainInput() {
				const el = this.$refs.input;
				// Only a + and digits reach the model, but the visitor may type a number
				// the way they would write it down; the spaces are simply not kept.
				el.value = el.value.replace(/[^\d+\s()-]/g, '');

				this.errorMessage = el.value.trim() && !this.isValid()
					? this.config.plainInvalidMessage
					: '';

				this.emitValue();
			},

			/** Writes a value the parent set while the plain box is in charge. */
			writePlain(next) {
				const el = this.$refs.input;
				if (el) {
					el.value = next;
				}
			},

			/** `+` and digits only - what goes to the CRM and into Members.Phone. */
			normalisePlain(raw) {
				return (raw || '').replace(/[^\d+]/g, '');
			},
			// Placeholder'daki rakam olmayan karakterleri (boşluk, tire, parantez)
			// girilen rakamların arasına serpiştirerek canlı biçimlendirme yapar.
			applyPlaceholderMask(raw, placeholder) {
				const digits = raw.replace(/\D/g, '');
				if (!placeholder || !digits) return digits;

				let out = '';
				let di = 0;
				for (let i = 0; i < placeholder.length && di < digits.length; i++) {
					if (/\d/.test(placeholder[i])) {
						out += digits[di++];
					} else if (di > 0) {
						out += placeholder[i];
					}
				}
				while (di < digits.length) out += digits[di++];
				return out;
			},
			isValid() {
				if (this.plainMode) {
					const el = this.$refs.input;
					const value = this.normalisePlain(el ? el.value : '');
					return value.length <= PLAIN_MAX_LENGTH && PLAIN_NUMBER.test(value);
				}
				if (!this.iti) return false;
				try {
					return this.iti.isValidNumber();
				} catch (err) {
					return false;
				}
			},
			emitValue() {
				const el = this.$refs.input;
				const number = this.plainMode
					? this.normalisePlain(el ? el.value : '')
					: (this.iti ? this.iti.getNumber() : '');

				this.lastEmitted = number;
				this.$emit('input', number);
				this.$emit('valid', this.isValid());
			},
			onBlur() {
				this.$emit('blur');
			},
			// Formun submit anında çağırması için: EAE'deki submit validasyonunun karşılığı.
			validate() {
				const el = this.$refs.input;
				const hasDigits = el && el.value.replace(/\D/g, '').length > 0;

				if (!hasDigits) {
					this.errorMessage = this.required ? this.config.requiredMessage : '';
					return !this.required;
				}
				if (!this.isValid()) {
					this.errorMessage = this.plainMode
						? this.config.plainInvalidMessage
						: this.config.invalidMessage;
					return false;
				}
				this.errorMessage = '';
				this.emitValue();
				return true;
			},
			/**
			 * Shows a message the component did not produce itself. The CRM validates the
			 * number more strictly than libphonenumber does - it wants a real Turkish
			 * operator prefix - so its refusal has to land under this field rather than in
			 * a toast that does not say which box is wrong.
			 */
			showError(message) {
				this.errorMessage = message || '';
			},
			getSelectedCountry() {
				return this.iti ? this.iti.getSelectedCountryData() : null;
			},
			/**
			 * Points the flag and dial code at a country the form has selected elsewhere.
			 * Takes the CRM's SAP code. Any digits already typed are kept and re-masked
			 * against the new country's placeholder.
			 *
			 * Seventeen of the CRM's 254 countries have no dial code to point at - Kuzey
			 * Kıbrıs and Abkhazia among them, along with Antarctica, NATO and the European
			 * Union. The field used to be left exactly as it was for those, which meant it
			 * kept the previously selected country and went on building the number from
			 * that dial code: a member who chose Abkhazia was stored, and delivered to the
			 * CRM, with an Algerian number. Now the control steps aside and the visitor
			 * types the whole number themselves.
			 *
			 * Still returns whether a dial code was found, for any caller that wants it.
			 */
			setCountry(sapCode) {
				if (!sapCode) return false;

				const code = SAP_TO_ISO2[sapCode.toLowerCase()] || sapCode.toLowerCase();

				// The flag control may not be on screen. Put it back before asking, because
				// it is the only thing that knows whether this code has a dial code; if it
				// turns out not to, the two calls below hand the field straight back.
				if (this.plainMode) {
					this.leavePlainMode();
				}

				if (!this.iti) return false;

				try {
					this.iti.setCountry(code);
				} catch (err) {
					this.enterPlainMode();
					return false;
				}

				// setCountry ignores a code it does not recognise instead of reporting it,
				// so read the selection back rather than trusting the call.
				const selected = this.iti.getSelectedCountryData();
				if (!selected || selected.iso2 !== code) {
					this.enterPlainMode();
					return false;
				}

				this.onInput();
				return true;
			}
		}
	};
</script>

<style lang="scss">
	.phone-input {
		.iti {
			width: 100%;
			display: flex;
		}

		.iti input.input {
			flex: 1;
			min-width: 0;
		}
	}

	.phone-input--error input.input {
		border-color: #f14668;
	}
</style>
